'use client';

import { Alert } from '@/components/alert/Alert';
import { RichText } from '@/components/richText/RichText';
import type { AlertSeverity, AlertVariant } from '@/types/alert';
import type { ComponentAlertStoryblok } from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { type FC, useState } from 'react';

const severityTheme: Record<AlertSeverity, 'light' | 'orange' | 'green' | 'blue'> = {
  error: 'light',
  warning: 'orange',
  success: 'green',
  info: 'blue',
};

function resolveAlertSeverity(severity?: string): AlertSeverity {
  if (severity === 'error' || severity === 'warning' || severity === 'success' || severity === 'info') {
    return severity;
  }

  return 'info';
}

const AlertBox: FC<{ blok: ComponentAlertStoryblok; appendUrlParams?: Record<string, string | number> }> = ({
  blok,
  appendUrlParams,
}) => {
  const [open, setOpen] = useState(true);
  const variant: AlertVariant = blok.style ? (blok.style as AlertVariant) : 'standard';
  const severity = resolveAlertSeverity(blok.severity);

  if (!open) {
    return null;
  }

  const editable = storyblokEditable(blok) as { className?: string } & Record<string, unknown>;
  const { className: blokClassName, ...editableRest } = editable;

  return (
    <Alert
      {...editableRest}
      className={classNames(blokClassName)}
      data-theme={severityTheme[severity]}
      severity={severity}
      variant={variant}
      id={blok.anchor || undefined}
      headline={blok.headline}
      closable={blok.closable}
      onClose={() => setOpen(false)}
    >
      <RichText text={blok.text} appendUrlParams={appendUrlParams} noLinkStyle />
    </Alert>
  );
};

export default AlertBox;
